/**
 * Supported real-time input normalization modes.
 * - 'identifier': Standard name-like fields & stream names (lowercase + whitespace to underscore).
 * - 'stream-field': Stream field/column names exception (whitespace to underscore, preserves casing).
 */
export type NormalizationMode = 'identifier' | 'stream-field'

/**
 * Normalizes identifier-like fields:
 * - 'identifier' (or true / default): converts to lowercase and replaces spaces with underscores.
 * - 'stream-field': replaces spaces with underscores while preserving letter casing.
 */
export function normalizeIdentifier(value: string, mode?: NormalizationMode | boolean): string {
	if (!value) {
		return ''
	}
	const spaced = value.replace(/\s+/g, '_')
	return mode === 'stream-field' ? spaced : spaced.toLowerCase()
}

export const NON_LOWERCASE_ALPHANUMERIC_PATTERN = /[^a-z0-9_]/
export const NON_ALPHANUMERIC_PATTERN = /[^a-zA-Z0-9_]/

/**
 * Checks if a value contains invalid characters for the given normalization mode.
 */
export function hasInvalidIdentifierChars(value: string, mode?: NormalizationMode | boolean): boolean {
	if (!mode || !value) {
		return false
	}
	const pattern = mode === 'stream-field' ? NON_ALPHANUMERIC_PATTERN : NON_LOWERCASE_ALPHANUMERIC_PATTERN
	return pattern.test(value)
}

/**
 * Returns a validation error message when a value contains
 * invalid characters for the given normalization mode, or null if valid.
 */
export function getNormalizationErrorMessage(value: string, mode?: NormalizationMode | boolean): string | null {
	if (!mode || !value) {
		return null
	}
	if (hasInvalidIdentifierChars(value, mode)) {
		return mode === 'stream-field'
			? 'Only letters, numbers, and underscores allowed'
			: 'Only lowercase letters, numbers, and underscores allowed'
	}
	return null
}

/** Help copy for any field that normalises as you type. Shown through `InlineHelpTooltip`. */
export const IDENTIFIER_HELP_TEXT =
	'Becomes an identifier, so it is lowercased and spaces become underscores. Letters first, 63 characters at most.'

/** PostgreSQL's identifier limit, which every name-like field inherits. */
export const MAX_IDENTIFIER_LENGTH = 63

/**
 * The shape `normalizeIdentifier` output must still satisfy. Normalizing handles case and spaces;
 * a leading digit and the remaining punctuation are the user's to fix, so they need a message.
 */
export const IDENTIFIER_PATTERN = /^[a-z][a-z0-9_]*$/

/**
 * Validates a name-like field against the convention, returning the message to show or null.
 * The message names no field, since it renders directly under the input it belongs to.
 */
export function validateIdentifier(value: string): string | null {
	if (value.length > MAX_IDENTIFIER_LENGTH) {
		return `Max ${MAX_IDENTIFIER_LENGTH} characters allowed`
	}
	if (!IDENTIFIER_PATTERN.test(value)) {
		return 'Must start with a letter. Only lowercase letters, numbers, and underscores allowed'
	}
	return null
}

/**
 * Escape regex metacharacters so a user-typed value is matched literally when
 * sent to a backend `regex` filter (i.e. plain-text substring search, not a
 * raw user-supplied regular expression). Unescaped input would otherwise cause
 * backend errors on invalid regex, or unintended over-broad matches.
 */
export function escapeRegexLiteral(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
